import { defaults, state } from '@frameio/graphql-client';
import type { TransferJobTaskReactiveMapState } from '@frameio/graphql-client/client-schema/transfers/state/transferJobTasks';

function isSameProgress(
  a: TransferJobTaskReactiveMapState['progress'],
  b: TransferJobTaskReactiveMapState['progress']
) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.bytesPerSecond === b.bytesPerSecond &&
    a.bytesSent === b.bytesSent &&
    a.bytesTotal === b.bytesTotal &&
    a.percentage === b.percentage &&
    a.remainingTime === b.remainingTime
  );
}

export function createOrUpdateTransferState(
  key: string,
  transferState: Partial<TransferJobTaskReactiveMapState>
) {
  if (!key) return;

  const currentOrInitialState = state.transfers.transferJobTasks.get(
    key,
    defaults.transfers.INITIAL_TRANSFER_JOB_TASK_STATE
  );

  /**
   * Make sure the new state is not a step back.
   * Could possibly be an out of order event/progress
   */
  if (
    !transferState.status ||
    currentOrInitialState?.status > transferState.status
  )
    return;

  /**
   * Encoding shares the UPLOADING status, so the status guard can't catch a
   * late encode event; once the real transfer clears isEncoding, don't let a
   * stale update flip it back to true.
   */
  if (
    transferState.isEncoding === true &&
    currentOrInitialState?.isEncoding === false
  )
    return;

  /**
   * Updates the task map with an updated state. Making sure to not override
   * with undefined values. `assetId`/`jobId` prefer the caller's values (the
   * old code kept only the current ids, so caller-passed ids never landed).
   */
  const nextState: TransferJobTaskReactiveMapState = {
    assetId: transferState.assetId ?? currentOrInitialState.assetId,
    jobId: transferState.jobId ?? currentOrInitialState.jobId,
    // @note: revisit if this does need to be updated on every `set` update.
    filePath: transferState.filePath ?? currentOrInitialState?.filePath,
    status: transferState.status ?? currentOrInitialState?.status,
    progress: transferState.progress ?? currentOrInitialState?.progress,
    isPaused: transferState.isPaused ?? currentOrInitialState?.isPaused,
    isEncoding: transferState.isEncoding ?? currentOrInitialState?.isEncoding,
  };

  /**
   * The map broadcasts on reference inequality and writers always pass fresh
   * objects, so skip no-op writes to avoid re-rendering subscribers.
   */
  if (
    nextState.assetId === currentOrInitialState.assetId &&
    nextState.jobId === currentOrInitialState.jobId &&
    nextState.filePath === currentOrInitialState.filePath &&
    nextState.status === currentOrInitialState.status &&
    nextState.isPaused === currentOrInitialState.isPaused &&
    nextState.isEncoding === currentOrInitialState.isEncoding &&
    isSameProgress(nextState.progress, currentOrInitialState.progress)
  )
    return;

  state.transfers.transferJobTasks.set(key, nextState);
}
